import React, { useState } from "react";
import { LinearProgress } from "@material-ui/core";
import * as XLSX from "xlsx";
import styled from "styled-components";
import API from "../../../../../app/utils/api";
import { WarningDialog } from "../../../../../app/components";
import { BUTTON_LABELS } from "../../../../../app/components/warning.dialog/messages";
import PrimaryButton from "../../../../../app/components/buttons/primary";
import OutlinedButton from "../../../../../app/components/buttons/outlined";
import FileUploader from "../../../../../app/components/file.uploader";
import CustomModal from "../../../../../app/components/modal.no.close";
import { UPLOAD_COST_MODAL_WARNING_MESSAGE } from "../../../../../app/components/warning.dialog/messages";
import { useScheduleEvents } from "../../../../../app/contexts/schedule.events.context";
import { useSnackbar } from "../../../../../app/contexts/snackbar.context";
import { processExcelFile } from "./process.cost.data";

const UploadCostModal = ({ onContinue, studyId, scheduleId, ...modalProps }) => {
  const { SOECountries } = useScheduleEvents();
  const { showSnackbar } = useSnackbar();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [countryData, setCountryData] = useState([]);
  const [countriesWithCosts, setCountriesWithCosts] = useState([]);

  const handleWarningConfirm = async (localCountryData = undefined) => {
    try {
      const response = await API.uploadCost(
        studyId,
        countryData.length === 0 ? localCountryData : countryData
      );

      const dataStatus = Number(response?.data?.[0]?.returnCode) || response?.status;
      const message = response?.data?.[0]?.returnDesc ?? "";
      if (response.status === 200 && dataStatus === 200) {
        showSnackbar("Country costs uploaded successfully", "success");
        setProgress(100);
        setStatus(message);
        setApiStatus(200);
      } else if (response.status === 200 && dataStatus !== 200) {
        showSnackbar("Failed to upload country costs", "error");
        setProgress(100);
        setStatus(message);
        setApiStatus(dataStatus);
      } else {
        showSnackbar("Failed to upload country costs", "error");
        setProgress(100);
        setStatus(message);
        setApiStatus(response.status);
        setUploadErrors([
          response.data?.message || "Unknown error occurred",
        ]);
      }
    } catch (error) {
      showSnackbar("Failed to upload country costs", "error");
      setProgress(100);
      setStatus("Upload failed.");
      setApiStatus("error");
      setUploadErrors([error.message || "Unknown error occurred"]);
    }

    setShowWarning(false);
  };

  const handleWarningCancel = () => {
    showSnackbar("Upload cancelled", "warning");
    setProgress(100);
    setStatus("Upload cancelled.");
    setApiStatus("warning");
    setShowWarning(false);
    setLoading(false);
  };

  const handleUpload = async (files) => {
    if (files.length === 0) return;

    setLoading(true);
    setProgress(0);
    setStatus("Processing country costs...");
    setUploadErrors([]);

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: "array" });

        const {
          countryData,
          totalValidSheets,
          countriesWithCosts: localCountriesWithCosts,
        } = processExcelFile(
          workbook,
          scheduleId,
          SOECountries,
        );
        setCountryData(countryData);
        setCountriesWithCosts(localCountriesWithCosts);

        if (totalValidSheets === 0) {
          setUploadErrors(["No valid data found in any sheet"]);
          setStatus("Upload failed - no valid data found.");
          showSnackbar("Upload failed - no valid data found.", "error");
          setProgress(100);
          setApiStatus("error");
          setLoading(false);
          return;
        }

        if (localCountriesWithCosts.length > 0) {
          setShowWarning(true);
          return;
        } else {
          await handleWarningConfirm(countryData);
        }
      };
      reader.readAsArrayBuffer(files[0]);
    } catch (error) {
      showSnackbar("Failed to process file", "error");
      setLoading(false);
      setUploadErrors([error.message]);
    }
  };

  const handleContinue = () => {
    setLoading(false);
    modalProps.onClose();
    onContinue();
  };

  return (
    <CustomModal
      size={700}
      {...modalProps}
      onClose={loading ? undefined : modalProps.onClose}
      aria-labelledby="Upload Country Costs"
      aria-describedby="upload-cost"
      title="Upload Country Costs"
    >
      <Container>
        {!loading && (
          <VContainer>
            <FileUploader
              acceptedFiles={{
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                  [],
              }}
              onFileUpload={handleUpload}
            />
          </VContainer>
        )}
        {loading && (
          <VContainer>
            {apiStatus === null && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                />
                <StatusText>{status}</StatusText>
              </>
            )}
            {apiStatus !== null && (
              <StatusContainer success={apiStatus === 200}>
                <StatusTitle>
                  {apiStatus === 200
                    ? "Upload complete."
                    : ""}
                </StatusTitle>
                {status && <StatusDetail>{status}</StatusDetail>}
              </StatusContainer>
            )}
            {uploadErrors.length > 0 && (
              <ErrorContainer>
                <ErrorTitle>Errors Found:</ErrorTitle>
                {uploadErrors.map((error, index) => (
                  <ErrorText key={index}>{error}</ErrorText>
                ))}
              </ErrorContainer>
            )}
          </VContainer>
        )}
        <ButtonContainer>
          {loading && apiStatus === 200 ? (
            <PrimaryButton onClick={handleContinue}>Continue</PrimaryButton>
          ) : (
            <OutlinedButton onClick={modalProps.onClose}>Cancel</OutlinedButton>
          )}
        </ButtonContainer>
      </Container>
      <WarningDialog
        open={showWarning}
        onConfirm={() => handleWarningConfirm()}
        onCancel={handleWarningCancel}
        message={UPLOAD_COST_MODAL_WARNING_MESSAGE(countriesWithCosts)}
        confirmLabel={BUTTON_LABELS.UPLOAD}
      />
    </CustomModal>
  );
};

export default UploadCostModal;

const VContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding-top: 0;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2em;
  padding: 1em;
  padding-bottom: 1em;
  padding-top: 0;
  max-height: 75vh;
  overflow-y: auto;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1em;
`;

const StatusText = styled.div`
  font-weight: 500;
  margin-bottom: 1em;
`;

const StatusContainer = styled.div`
  padding: 1em;
  border-radius: 4px;
  text-align: center;
  background-color: ${(props) => (props.success ? "#e8f5e9" : "#ffebee")};
  color: ${(props) => (props.success ? "#2e7d32" : "#c62828")};
`;

const StatusTitle = styled.div`
  font-weight: 500;
  margin-bottom: 1em;
`;

const StatusDetail = styled.div`
  font-size: 0.9em;
  white-space: pre-line;
  line-height: 1.5;
`;

const ErrorContainer = styled.div`
  background-color: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 4px;
  padding: 1em;
  margin-top: 1em;
`;

const ErrorTitle = styled.div`
  color: #d32f2f;
  font-weight: 500;
  margin-bottom: 0.5em;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 0.9em;
  margin-bottom: 0.25em;
`;
